import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import storyboards from './storyboards.json';
import { RESULT_LABELS } from './scoreResults';
import { loadReaction } from './preload';
import './score-reactions.css';

export const ScoreReactionOverlay = ({ result, hole, onComplete }) => {
  const spec = storyboards[result];
  const reduced = useReducedMotion();
  const [ready, setReady] = useState(false);
  const [frame, setFrame] = useState(0);
  const done = useRef(false);
  const finishRef = useRef(onComplete);
  finishRef.current = onComplete;
  const finish = () => { if (!done.current) { done.current = true; finishRef.current(); } };

  useEffect(() => {
    let active = true;
    let raf, watchdog;
    const finishOnce = () => { if (active && !done.current) { done.current = true; finishRef.current(); } };
    if (!spec) { finishOnce(); return undefined; }
    const escape = event => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); finishOnce(); } };
    const visibility = () => { if (document.hidden) finishOnce(); };
    document.addEventListener('keydown', escape, true);
    document.addEventListener('visibilitychange', visibility);
    loadReaction(result).then(() => {
      if (!active || done.current) return;
      setReady(true);
      const duration = reduced ? 900 : spec.duration * 1000;
      const start = performance.now();
      const tick = now => {
        if (!active || done.current) return;
        const seconds = (now - start) / 1000;
        const index = reduced ? (result === 'albatross' ? 4 : result === 'ace' ? 8 : result === 'birdie' ? 8 : 6) : spec.starts.reduce((last, time, i) => seconds >= time ? i : last, 0);
        setFrame(index);
        if (now - start >= duration) finishOnce();
        else raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      watchdog = setTimeout(finishOnce, duration + 250);
    }).catch(finishOnce);
    return () => {
      active = false; cancelAnimationFrame(raf); clearTimeout(watchdog);
      document.removeEventListener('keydown', escape, true);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [result, reduced, spec]);

  if (!ready || !spec) return null;
  return createPortal(<div className="score-reaction-overlay" data-testid="score-reaction-overlay" data-result={result} data-reduced-motion={Boolean(reduced)}>
    <div role="status" aria-live="polite" className="sr-only" data-testid="score-reaction-announcement">{hole ? `Hole ${hole}: ` : ''}{RESULT_LABELS[result]}</div>
    <motion.div className="score-reaction-stage" data-testid="score-reaction-stage"
      initial={reduced ? false : { opacity: 0, y: 24, scale: .88 }}
      animate={reduced ? { opacity: 1 } : { opacity: [0, 1, 1, 1, 0], y: [24, 0, -4, 0, -20], scale: [.88, 1, 1.02, 1, .95] }}
      transition={{ duration: reduced ? 0 : spec.duration, times: [0, .07, .5, .93, 1], ease: 'easeInOut' }}>
      <div className="score-reaction-art" aria-hidden="true" data-testid="score-reaction-art" data-frame={frame}
        style={{ backgroundImage: `url(${spec.file})`, backgroundSize: `${spec.frames * 100}% 100%`, backgroundPosition: `${frame / (spec.frames - 1) * 100}% 0` }} />
    </motion.div>
    <button type="button" className="score-reaction-dismiss" onClick={finish} aria-label="Skip score animation" title="Skip animation" data-testid="score-reaction-skip"><X size={20} /></button>
  </div>, document.body);
};